import { activeMassForModel, isCocaine } from "@/domain/powderDose";
import type { KBTemplate } from "@/lib/pharmacologyKnowledgeBase";
import { normalize } from "@/lib/pharmacology/aliasResolver";
import { unitToMinutes } from "@/lib/units";

export type PkParameterSource = "formulation" | "canonical" | "knowledge";
export type PkBodyModel = "first_order_absorption" | "elimination_envelope" | "zero_order_alcohol";
export type PkConfidence = "baixa" | "moderada" | "alta";

export interface BodyLoadDoseInput {
  id?: string | null;
  substance_id?: string | null;
  substance_name?: string | null;
  actual_time?: string | Date | null;
  dose_amount?: number | string | null;
  dose_unit?: string | null;
  route?: string | null;
  formulation_id?: string | null;
  formulation_name?: string | null;
  substances?: Record<string, any> | null;
  substance_formulations?: Record<string, any> | null;
  formulation?: Record<string, any> | string | null;
  beverage_container_volume_ml?: number | string | null;
  beverage_abv_percent?: number | string | null;
  beverage_consumed_value?: number | string | null;
  beverage_consumed_unit?: string | null;
  beverage_consumed_ml?: number | string | null;
  ethanol_grams?: number | string | null;
  powder_mass_mg?: number | string | null;
  purity_status?: string | null;
  purity_percent?: number | string | null;
  estimated_active_mg?: number | string | null;
}

export interface ResolvedPkParameters {
  canonicalName: string;
  halfLifeMinMinutes: number;
  halfLifeMaxMinutes: number;
  halfLifeMidMinutes: number;
  tmaxMinutes: number | null;
  source: PkParameterSource;
  model: PkBodyModel;
  confidence: PkConfidence;
  notes: string[];
}

export interface ModeledDoseAmount {
  id: string;
  canonicalName: string;
  at: string;
  originalAmount: number;
  originalUnit: string;
  remainingLow: number;
  remainingMid: number;
  remainingHigh: number;
  remainingFraction: number;
  model: PkBodyModel;
  parameterSource: PkParameterSource;
  confidence: PkConfidence;
}

export interface BodyLoadGroup {
  key: string;
  canonicalName: string;
  unit: string;
  unitFamily: string;
  totalDosed: number;
  remainingLow: number;
  remainingMid: number;
  remainingHigh: number;
  remainingFraction: number;
  modeledDoseCount: number;
  confidence: PkConfidence;
  models: PkBodyModel[];
  sources: PkParameterSource[];
  doses: ModeledDoseAmount[];
  notes: string[];
}

export interface BodyLoadResult {
  generatedAt: string;
  groups: BodyLoadGroup[];
  modeledDoseCount: number;
  skippedDoseCount: number;
  futureDoseCount: number;
  notes: string[];
}

export interface BodyLoadOptions {
  now?: Date;
  knowledge?: KBTemplate[];
  bodyWeightKg?: number | null;
}

function numberOrNull(value: unknown): number | null {
  if (value === "" || value == null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function positive(value: unknown): number | null {
  const parsed = numberOrNull(value);
  return parsed != null && parsed > 0 ? parsed : null;
}

function normalizeUnit(unit?: string | null) {
  const raw = String(unit ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  const compact = raw.replace(/[.\s_-]+/g, "");
  if (["µg", "μg", "ug", "mcg", "micrograma", "microgramas"].includes(compact)) return "µg";
  if (["mg", "miligrama", "miligramas"].includes(compact)) return "mg";
  if (["g", "grama", "gramas", "getanol", "gramaetanol", "gramasetanol"].includes(compact)) return "g";
  if (["ng", "nanograma", "nanogramas"].includes(compact)) return "ng";
  if (["ml", "mililitro", "mililitros"].includes(compact)) return "mL";
  return String(unit ?? "unidade").trim() || "unidade";
}

function massToMgFactor(unit: string): number | null {
  switch (normalizeUnit(unit)) {
    case "g": return 1000;
    case "mg": return 1;
    case "µg": return 0.001;
    case "ng": return 0.000001;
    default: return null;
  }
}

function unitFamily(unit: string) {
  return massToMgFactor(unit) != null ? "mass" : normalizeUnit(unit).toLowerCase();
}

function convertAmount(value: number, from: string, to: string): number | null {
  const fromFactor = massToMgFactor(from);
  const toFactor = massToMgFactor(to);
  if (fromFactor != null && toFactor != null) return (value * fromFactor) / toFactor;
  return normalizeUnit(from) === normalizeUnit(to) ? value : null;
}

function timeRangeMinutes(entity: any, prefix: string): { min: number; max: number; mid: number } | null {
  if (!entity || typeof entity !== "object") return null;
  const minRaw = positive(entity[`${prefix}_min_value`] ?? entity[`${prefix}_min`]);
  const maxRaw = positive(entity[`${prefix}_max_value`] ?? entity[`${prefix}_max`]);
  if (minRaw == null && maxRaw == null) return null;
  const unit = String(entity[`${prefix}_unit`] ?? "minutos");
  const min = unitToMinutes(minRaw ?? maxRaw!, unit);
  const max = unitToMinutes(maxRaw ?? minRaw!, unit);
  if (!(min > 0) || !(max > 0)) return null;
  const lo = Math.min(min, max);
  const hi = Math.max(min, max);
  return { min: lo, max: hi, mid: (lo + hi) / 2 };
}

function tmaxFromPkProfile(entity: any): number | null {
  if (!entity || typeof entity !== "object") return null;
  const direct = timeRangeMinutes(entity, "tmax");
  if (direct) return direct.mid;
  const profile = entity.pk_profile;
  if (!profile || typeof profile !== "object" || Array.isArray(profile)) return null;
  const fromProfile = timeRangeMinutes(profile, "tmax");
  if (fromProfile) return fromProfile.mid;
  const hours = positive((profile as any).tmax_hours ?? (profile as any).t_max_hours);
  if (hours != null) return hours * 60;
  const minutes = positive((profile as any).tmax_minutes ?? (profile as any).t_max_minutes);
  return minutes;
}

function allTemplateNames(template: KBTemplate): string[] {
  return [
    template.name,
    template.generic_name,
    ...(template.brand_names ?? []),
    ...(template.brands ?? []),
    ...(template.international_brand_names ?? []),
    ...(template.reference_names ?? []),
    ...(template.match ?? []),
    ...(template.synonyms ?? []),
  ].filter(Boolean).map(String);
}

export function findKnowledgeTemplate(name: string, knowledge: KBTemplate[]): KBTemplate | null {
  const target = normalize(name);
  if (!target) return null;
  const exact = knowledge.find((template) => allTemplateNames(template).some((candidate) => normalize(candidate) === target));
  if (exact) return exact;
  return knowledge.find((template) => normalize(template.name).includes(target) || target.includes(normalize(template.name))) ?? null;
}

function canonicalNameForDose(dose: BodyLoadDoseInput, template?: KBTemplate | null) {
  return String(template?.name ?? dose.substances?.name ?? dose.substance_name ?? "Substância").trim();
}

function sourceConfidence(source: PkParameterSource, template?: KBTemplate | null): PkConfidence {
  if (source === "formulation") return "alta";
  if (source === "canonical") return "moderada";
  if (template?.needs_review || /revis/i.test(String(template?.review_status ?? ""))) return "baixa";
  return template?.confidence === "high" ? "moderada" : "baixa";
}

function isAlcoholDose(dose: BodyLoadDoseInput) {
  const name = normalize(String(dose.substances?.name ?? dose.substance_name ?? ""));
  return dose.ethanol_grams != null || /(^|\s)(alcool|alcohol|etanol|ethanol)(\s|$)/.test(name);
}

export function resolvePkParameters(dose: BodyLoadDoseInput, knowledge: KBTemplate[] = []): ResolvedPkParameters | null {
  if (isAlcoholDose(dose)) return null;
  const rawName = String(dose.substances?.name ?? dose.substance_name ?? "").trim();
  const template = findKnowledgeTemplate(rawName, knowledge);
  const formulation = typeof dose.formulation === "object" && dose.formulation ? dose.formulation : dose.substance_formulations;
  const candidates: Array<{ source: PkParameterSource; entity: any }> = [
    { source: "formulation", entity: formulation },
    { source: "canonical", entity: dose.substances },
    { source: "knowledge", entity: template },
  ];
  for (const candidate of candidates) {
    const halfLife = timeRangeMinutes(candidate.entity, "half_life");
    if (!halfLife) continue;
    const tmaxMinutes = tmaxFromPkProfile(candidate.entity) ?? (candidate.source !== "knowledge" ? tmaxFromPkProfile(template) : null);
    const model: PkBodyModel = tmaxMinutes != null ? "first_order_absorption" : "elimination_envelope";
    const notes: string[] = [];
    if (model === "elimination_envelope") notes.push("Sem Tmax farmacocinético estruturado: usa envelope de eliminação, não curva de absorção.");
    if (candidate.source === "knowledge" && template?.needs_review) notes.push("Parâmetro vindo de conhecimento marcado para revisão clínica.");
    return {
      canonicalName: canonicalNameForDose(dose, template),
      halfLifeMinMinutes: halfLife.min,
      halfLifeMaxMinutes: halfLife.max,
      halfLifeMidMinutes: halfLife.mid,
      tmaxMinutes,
      source: candidate.source,
      model,
      confidence: sourceConfidence(candidate.source, template),
      notes,
    };
  }
  return null;
}

export function eliminationRateFromHalfLife(halfLifeMinutes: number) {
  if (!(halfLifeMinutes > 0)) return null;
  return Math.LN2 / halfLifeMinutes;
}

export function estimateAbsorptionRateFromTmax(tmaxMinutes: number, ke: number): number | null {
  if (!(tmaxMinutes > 0) || !(ke > 0)) return null;
  const target = tmaxMinutes;
  let bestKa: number | null = null;
  let bestError = Number.POSITIVE_INFINITY;
  for (let i = 0; i <= 480; i++) {
    const exponent = -4 + (8 * i) / 480;
    const ka = ke * Math.pow(10, exponent);
    if (!(ka > 0) || Math.abs(ka - ke) / ke < 1e-5) continue;
    const predicted = Math.log(ka / ke) / (ka - ke);
    if (!(predicted > 0) || !Number.isFinite(predicted)) continue;
    const error = Math.abs(predicted - target);
    if (error < bestError) {
      bestError = error;
      bestKa = ka;
    }
  }
  return bestKa;
}

export function fractionRemainingEliminationOnly(elapsedMinutes: number, halfLifeMinutes: number) {
  if (elapsedMinutes < 0) return 1;
  const ke = eliminationRateFromHalfLife(halfLifeMinutes);
  if (ke == null) return null;
  return Math.max(0, Math.min(1, Math.exp(-ke * elapsedMinutes)));
}

export function fractionRemainingWithAbsorption(elapsedMinutes: number, halfLifeMinutes: number, tmaxMinutes: number) {
  if (elapsedMinutes < 0) return 1;
  const ke = eliminationRateFromHalfLife(halfLifeMinutes);
  if (ke == null) return null;
  const ka = estimateAbsorptionRateFromTmax(tmaxMinutes, ke);
  if (ka == null) return fractionRemainingEliminationOnly(elapsedMinutes, halfLifeMinutes);
  const t = elapsedMinutes;
  if (Math.abs(ka - ke) / Math.max(ka, ke) < 1e-5) return Math.max(0, Math.min(1, Math.exp(-ke * t) * (1 + ke * t)));
  const gut = Math.exp(-ka * t);
  const central = (ka / (ka - ke)) * (Math.exp(-ke * t) - Math.exp(-ka * t));
  return Math.max(0, Math.min(1, gut + central));
}

export function modeledRemainingFraction(elapsedMinutes: number, params: ResolvedPkParameters) {
  const compute = (halfLife: number) => params.model === "first_order_absorption" && params.tmaxMinutes != null
    ? fractionRemainingWithAbsorption(elapsedMinutes, halfLife, params.tmaxMinutes)
    : fractionRemainingEliminationOnly(elapsedMinutes, halfLife);
  const lowCandidate = compute(params.halfLifeMinMinutes);
  const midCandidate = compute(params.halfLifeMidMinutes);
  const highCandidate = compute(params.halfLifeMaxMinutes);
  if (lowCandidate == null || midCandidate == null || highCandidate == null) return null;
  const values = [lowCandidate, midCandidate, highCandidate];
  return { low: Math.min(...values), mid: midCandidate, high: Math.max(...values) };
}

export function alcoholEliminationRates(bodyWeightKg: number | null | undefined) {
  if (bodyWeightKg == null || !Number.isFinite(bodyWeightKg) || bodyWeightKg < 30 || bodyWeightKg > 300) return null;
  return {
    slowGPerHour: 0.07 * bodyWeightKg,
    midGPerHour: 0.10 * bodyWeightKg,
    fastGPerHour: 0.18 * bodyWeightKg,
  };
}

export function modelAlcoholRemaining(ethanolGrams: number, elapsedMinutes: number, bodyWeightKg: number | null | undefined) {
  const rates = alcoholEliminationRates(bodyWeightKg);
  if (!rates || !(ethanolGrams > 0) || elapsedMinutes < 0) return null;
  const hours = elapsedMinutes / 60;
  const remainingLow = Math.max(0, ethanolGrams - rates.fastGPerHour * hours);
  const remainingMid = Math.max(0, ethanolGrams - rates.midGPerHour * hours);
  const remainingHigh = Math.max(0, ethanolGrams - rates.slowGPerHour * hours);
  return {
    low: remainingLow,
    mid: remainingMid,
    high: remainingHigh,
    fraction: Math.max(0, Math.min(1, remainingMid / ethanolGrams)),
    rates,
  };
}

function weakestConfidence(values: PkConfidence[]): PkConfidence {
  if (values.includes("baixa")) return "baixa";
  if (values.includes("moderada")) return "moderada";
  return "alta";
}

export function buildBodyLoad(doses: BodyLoadDoseInput[], options: BodyLoadOptions = {}): BodyLoadResult {
  const now = options.now ?? new Date();
  const knowledge = options.knowledge ?? [];
  const modeled: ModeledDoseAmount[] = [];
  let skippedDoseCount = 0;
  let futureDoseCount = 0;
  let skippedAlcoholForWeight = 0;
  let skippedUnknownActiveMass = 0;

  for (const [index, dose] of doses.entries()) {
    const at = dose.actual_time ? new Date(dose.actual_time) : null;
    if (!at || !Number.isFinite(at.getTime())) {
      skippedDoseCount++;
      continue;
    }
    const elapsedMinutes = (now.getTime() - at.getTime()) / 60_000;
    if (elapsedMinutes < 0) {
      futureDoseCount++;
      continue;
    }

    if (isAlcoholDose(dose)) {
      const ethanolGrams = positive(dose.ethanol_grams ?? dose.dose_amount);
      if (ethanolGrams == null) {
        skippedDoseCount++;
        continue;
      }
      const remaining = modelAlcoholRemaining(ethanolGrams, elapsedMinutes, options.bodyWeightKg);
      if (!remaining) {
        skippedDoseCount++;
        skippedAlcoholForWeight++;
        continue;
      }
      modeled.push({
        id: String(dose.id ?? `dose-${index}`),
        canonicalName: "Álcool",
        at: at.toISOString(),
        originalAmount: ethanolGrams,
        originalUnit: "g",
        remainingLow: remaining.low,
        remainingMid: remaining.mid,
        remainingHigh: remaining.high,
        remainingFraction: remaining.fraction,
        model: "zero_order_alcohol",
        parameterSource: "knowledge",
        confidence: "moderada",
      });
      continue;
    }

    const activeMass = activeMassForModel(dose);
    const amount = activeMass ? positive(activeMass.amount) : null;
    if (amount == null) {
      if (isCocaine(dose.substances?.name ?? dose.substance_name ?? "") && positive(dose.powder_mass_mg ?? dose.dose_amount) != null) {
        skippedUnknownActiveMass++;
      }
      skippedDoseCount++;
      continue;
    }
    const params = resolvePkParameters(dose, knowledge);
    if (!params) {
      skippedDoseCount++;
      continue;
    }
    const remaining = modeledRemainingFraction(elapsedMinutes, params);
    if (!remaining) {
      skippedDoseCount++;
      continue;
    }
    modeled.push({
      id: String(dose.id ?? `dose-${index}`),
      canonicalName: params.canonicalName,
      at: at.toISOString(),
      originalAmount: amount,
      originalUnit: normalizeUnit(activeMass!.unit),
      remainingLow: amount * remaining.low,
      remainingMid: amount * remaining.mid,
      remainingHigh: amount * remaining.high,
      remainingFraction: remaining.mid,
      model: params.model,
      parameterSource: params.source,
      confidence: params.confidence,
    });
  }

  const grouped = new Map<string, BodyLoadGroup>();
  for (const dose of modeled) {
    const family = unitFamily(dose.originalUnit);
    const canonicalKey = normalize(dose.canonicalName).replace(/\s+/g, "_");
    const key = `${canonicalKey}|${family}`;
    const current = grouped.get(key);
    if (!current) {
      grouped.set(key, {
        key,
        canonicalName: dose.canonicalName,
        unit: dose.originalUnit,
        unitFamily: family,
        totalDosed: dose.originalAmount,
        remainingLow: dose.remainingLow,
        remainingMid: dose.remainingMid,
        remainingHigh: dose.remainingHigh,
        remainingFraction: dose.remainingFraction,
        modeledDoseCount: 1,
        confidence: dose.confidence,
        models: [dose.model],
        sources: [dose.parameterSource],
        doses: [dose],
        notes: [],
      });
      continue;
    }
    const amount = convertAmount(dose.originalAmount, dose.originalUnit, current.unit);
    const low = convertAmount(dose.remainingLow, dose.originalUnit, current.unit);
    const mid = convertAmount(dose.remainingMid, dose.originalUnit, current.unit);
    const high = convertAmount(dose.remainingHigh, dose.originalUnit, current.unit);
    if (amount == null || low == null || mid == null || high == null) {
      skippedDoseCount++;
      continue;
    }
    current.totalDosed += amount;
    current.remainingLow += low;
    current.remainingMid += mid;
    current.remainingHigh += high;
    current.modeledDoseCount += 1;
    current.confidence = weakestConfidence([current.confidence, dose.confidence]);
    current.models = Array.from(new Set([...current.models, dose.model]));
    current.sources = Array.from(new Set([...current.sources, dose.parameterSource]));
    current.doses.push(dose);
    current.remainingFraction = current.totalDosed > 0 ? current.remainingMid / current.totalDosed : 0;
  }

  const groups = Array.from(grouped.values())
    .map((group) => ({
      ...group,
      remainingFraction: group.totalDosed > 0 ? group.remainingMid / group.totalDosed : 0,
      notes: [
        ...(group.models.includes("elimination_envelope") ? ["Ao menos uma dose usa envelope de eliminação por falta de Tmax farmacocinético estruturado."] : []),
        ...(group.models.includes("zero_order_alcohol") ? ["Álcool usa faixa de eliminação de ordem zero ajustada ao peso; é estimativa de etanol não eliminado, não alcoolemia."] : []),
        ...(group.sources.includes("knowledge") && !group.models.includes("zero_order_alcohol") ? ["Ao menos um parâmetro veio da biblioteca de conhecimento, não de uma formulação clínica específica."] : []),
      ],
    }))
    .filter((group) => group.remainingMid > group.totalDosed * 0.001)
    .sort((a, b) => b.remainingFraction - a.remainingFraction || b.remainingMid - a.remainingMid);

  const notes = [
    "Estimativa farmacocinética de quantidade parent-equivalente ainda não eliminada; não é concentração sérica nem intensidade subjetiva.",
    "Metabólitos ativos, tolerância e farmacodinâmica não são somados automaticamente ao valor de massa.",
    ...(skippedUnknownActiveMass > 0 ? [`${skippedUnknownActiveMass} registro(s) de cocaína/material em pó têm pureza desconhecida; a massa do material foi preservada, mas não convertida em massa ativa nem carga corporal.`] : []),
    ...(skippedAlcoholForWeight > 0 ? [`${skippedAlcoholForWeight} registro(s) de álcool ficaram sem curva de eliminação porque o peso corporal não estava disponível ou era inválido.`] : []),
    ...(skippedDoseCount > 0 ? [`${skippedDoseCount} dose(s) ficaram sem estimativa por falta de dose, horário, unidade compatível, parâmetro PK ou peso necessário.`] : []),
    ...(futureDoseCount > 0 ? [`${futureDoseCount} dose(s) futuras foram excluídas do cálculo.`] : []),
  ];

  return { generatedAt: now.toISOString(), groups, modeledDoseCount: modeled.length, skippedDoseCount, futureDoseCount, notes };
}
